import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useAcceptInvite, useInviteLookup } from '../lib/queries';

export function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>();
  const auth = useAuth();
  const navigate = useNavigate();
  const lookup = useInviteLookup(token);
  const accept = useAcceptInvite();
  const [err, setErr] = useState<string | null>(null);

  if (lookup.isLoading || auth.loading) {
    return <CenterText>Looking up your invite…</CenterText>;
  }

  if (lookup.error) {
    return (
      <Wrap eyebrow="Invite" title="Couldn't load this invite.">
        <p className="font-editorial italic text-[15px] text-ink-soft mb-6">
          {(lookup.error as Error).message}
        </p>
        <Link to="/" className="btn-text">Go home</Link>
      </Wrap>
    );
  }

  const invite = lookup.data;
  if (!invite) {
    return (
      <Wrap eyebrow="Invite" title="This invite isn't valid.">
        <p className="font-editorial italic text-[15px] text-ink-soft mb-6 max-w-md">
          The link may be mistyped, or the invite may have been removed. Ask the
          person who shared it for a fresh one.
        </p>
        <Link to="/" className="btn-text">Go home</Link>
      </Wrap>
    );
  }

  if (invite.accepted_at) {
    return (
      <Wrap eyebrow="Invite" title="This invite was already accepted.">
        <p className="font-editorial italic text-[15px] text-ink-soft mb-6 max-w-md">
          If you're already signed in to that account, you can go to your
          dashboard to find the workspace.
        </p>
        <Link to="/dashboard" className="btn-text">Open dashboard</Link>
      </Wrap>
    );
  }

  if (new Date(invite.expires_at).getTime() < Date.now()) {
    return (
      <Wrap eyebrow="Invite" title="This invite has expired.">
        <p className="font-editorial italic text-[15px] text-ink-soft mb-6 max-w-md">
          Invites expire after 14 days. Ask the workspace owner to send a new
          one.
        </p>
        <Link to="/" className="btn-text">Go home</Link>
      </Wrap>
    );
  }

  const next = `/invite/${token}`;
  const orgName = invite.org_name;

  // Not signed in — prompt to sign in or sign up, preserving the invite URL.
  if (!auth.user) {
    return (
      <Wrap
        eyebrow="You've been invited"
        title={
          <>
            Join{' '}
            <em className="italic text-accent">{orgName}</em>{' '}
            on Prajna Innovates.
          </>
        }
      >
        <p className="font-editorial text-[16px] text-ink-soft leading-relaxed mb-2 max-w-xl">
          You'll join as <strong className="font-medium">{invite.role}</strong>.
          Sign in (or create an account) with{' '}
          <strong className="font-medium">{invite.email}</strong> — or any email
          you already use — and you'll be added automatically.
        </p>
        <p className="font-editorial italic text-[13px] text-ink-muted mb-7">
          Expires {new Date(invite.expires_at).toLocaleDateString()}.
        </p>
        <div className="flex gap-4 flex-wrap">
          <Link
            to={`/signin?next=${encodeURIComponent(next)}`}
            className="btn-text"
          >
            Sign in →
          </Link>
          <Link
            to={`/signup?next=${encodeURIComponent(next)}`}
            className="btn-text"
            style={{ borderBottomColor: 'var(--rule)' }}
          >
            Create an account →
          </Link>
        </div>
      </Wrap>
    );
  }

  async function onAccept() {
    if (!token) return;
    setErr(null);
    try {
      const orgId = await accept.mutateAsync(token);
      navigate(`/workspace/${orgId}`, { replace: true });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  // Signed in — show the accept button.
  return (
    <Wrap
      eyebrow="Accept invite"
      title={
        <>
          Join{' '}
          <em className="italic text-accent">{orgName}</em>{' '}
          as {invite.role}.
        </>
      }
    >
      <p className="font-editorial text-[16px] text-ink-soft leading-relaxed mb-2 max-w-xl">
        You're signed in as <strong className="font-medium">{auth.user.email}</strong>.
        Click below to accept the invite — you'll be added to the workspace
        immediately.
      </p>
      <p className="font-editorial italic text-[13px] text-ink-muted mb-7">
        Expires {new Date(invite.expires_at).toLocaleDateString()}.
      </p>

      {err && (
        <div className="border-l-2 border-[#a02525] pl-4 py-2 mb-6 font-editorial italic text-[14px] text-[#a02525]">
          {err}
        </div>
      )}

      <div className="flex gap-4 flex-wrap items-baseline">
        <button
          type="button"
          onClick={onAccept}
          disabled={accept.isPending}
          className="btn-text disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {accept.isPending ? 'Joining…' : `Accept and join ${orgName} →`}
        </button>
        <Link
          to="/dashboard"
          className="btn-text"
          style={{ borderBottomColor: 'var(--rule)' }}
        >
          Not now
        </Link>
      </div>
    </Wrap>
  );
}

function Wrap({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string;
  title: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="max-w-3xl mx-auto px-6 py-16 md:py-24">
      <div className="eyebrow mb-4">{eyebrow}</div>
      <h1 className="editorial-h1 mb-6">{title}</h1>
      {children}
    </div>
  );
}

function CenterText({ children }: { children: React.ReactNode }) {
  return (
    <div className="max-w-md mx-auto px-6 py-24 text-center font-editorial italic text-ink-muted">
      {children}
    </div>
  );
}
